import Header from "@components/Header/Header"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import ConversionRateCard from "@components/ConversionRateCard/ConversionRateCard"
import { CRIMSON_500, DARK_VIOLET, GRAYSCALE_400, WHITE } from "@styles/colors"
import { INTER } from "@styles/fonts"
import ButtonWithIcon from "@components/ButtonWithIcon/ButtonWithIcon"
import QRIcon from "@components/QRIcon/QRIcon"
import BottomSheet from "@components/BottomSheet/BottomSheet"
import BottomSheetHeader from "@components/BottomSheetHeader/BottomSheetHeader"
import { QRCodeSVG } from "qrcode.react"
import { useState, useRef } from "react"
import ShareIcon from "@components/ShareIcon/ShareIcon"
import AlertBox from "@components/AlertBox/AlertBox"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import html2canvas from "html2canvas"
import {
  Container,
  Content,
  DepositCard,
  CardHeader,
  CryptoIcon,
  NameContainer,
  CryptoName,
  NetworkName,
  CardBody,
  CardFooter,
  FooterText,
  QRCodeContainer,
  ShareButtonContainer,
  ButtonContainer,
  CardContentWrapper,
  ExchangeRateRow,
  CurrencyPair,
  CurrencyLabel,
  CurrencyValue,
  ExchangeIconWrapper,
  ValidityRow,
  ValidityInfo,
  ValidityLabel,
  FixedQuoteButtonWrapper,
  AddressesWrapper
} from "./TopUpCryptoDepositAddress.styles"
import CopyableAddress from "@components/CopyableAddress/CopyableAddress"
import { useLocation } from "react-router"
import { toCurrencyFormat, toCurrencyFormatWithDecimal, roundAmount } from "@lib/utils"
import * as Sentry from "@sentry/react"
import CountdownTimer from "@components/CountdownTimer/CountdownTimer"
import useExchangeRate from "@hooks/useExchangeRate"

const TopUpCryptoDepositAddress = () => {
  const location = useLocation()
  const assetId = location.state?.id
  const cryptoName = location.state?.cryptoName
  const network = location.state?.network
  const iconUrl = location.state?.iconUrl
  const depositAddress = location.state?.depositAddress
  const initialRate = location.state?.rate
  const ticker = location.state?.ticker
  const decimal = location.state?.decimal ?? location.state?.uiDecimal ?? 8
  const amount = location.state?.amount
  const isFromPreview = !!amount
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const qrCodeRef = useRef<HTMLDivElement>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [rate, setRate] = useState(initialRate)
  const { getExchangeRate, isLoading: isRefreshing } = useExchangeRate()

  const handleRefreshRate = async () => {
    try {
      // Use the amount from preview, or default to 1 if not from preview
      const refreshAmount = amount || '1'

      // For this repo, we use 'variable-jidr' or 'fixed-jidr' mode
      // If from preview, use fixed mode, otherwise use variable mode with amount 1
      const mode = isFromPreview ? 'fixed-jidr' : 'variable-jidr'

      const newRate = await getExchangeRate(assetId, refreshAmount, mode)

      if (newRate && newRate.ex_rate) {
        setRate(newRate)
        console.log('Rate refreshed successfully', newRate)
      }
    } catch (error) {
      Sentry.captureException(error)
      console.error('Failed to refresh rate', error)
    }
  }

  const captureAndShareQRCode = async () => {
    if (!qrCodeRef.current || isCapturing) return

    try {
      setIsCapturing(true)

      // Capture only the QR code as an image
      const canvas = await html2canvas(qrCodeRef.current, {
        backgroundColor: null, // Use the container's background color
        scale: 3, // Higher scale for better quality
        logging: false,
        useCORS: true // To handle images from other domains
      })

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob as Blob)
        }, 'image/png', 0.95) // High quality PNG
      })

      // Create file from blob
      const fileName = `${cryptoName.toLowerCase()}-deposit-address-qr-${new Date().getTime()}.png`
      const file = new File([blob], fileName, { type: 'image/png' })

      // Try to use Web Share API if available
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: t('topUpCrypto.depositAddress.shareTitle', { crypto: cryptoName }),
          text: t('topUpCrypto.depositAddress.shareText', { address: depositAddress })
        })
      } else {
        // Fallback to download if sharing is not supported
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsCapturing(false)
    }
  }

  // Normalize validTime to ensure it's a finite number
  const normalizedValidTime = Number(rate?.valid_time)
  const validTime = Number.isFinite(normalizedValidTime) ? normalizedValidTime : 0

  return (
    <Container>
      <Header title={t('topUpCrypto.depositAddress.header')} showBackButton onBackClick={() => navigate('/top-up-crypto-select-asset')} />

      <Content>
        <CardContentWrapper>
          <DepositCard>
            <CardHeader>
              <CryptoIcon
                src={iconUrl}
                alt={cryptoName}
              />
              <NameContainer>
                <CryptoName>{cryptoName}</CryptoName>
                <NetworkName>{network}</NetworkName>
              </NameContainer>
            </CardHeader>
            <ExchangeRateRow>
              <CurrencyPair>
                <CurrencyLabel>{(ticker || 'N/A').toUpperCase()}</CurrencyLabel>
                <CurrencyValue>{" 1"}</CurrencyValue>
              </CurrencyPair>
              <ExchangeIconWrapper>
                <ExchangeRateIcon />
              </ExchangeIconWrapper>
              <CurrencyPair>
                <CurrencyLabel>{"JIDR "}</CurrencyLabel>
                <CurrencyValue>
                  {rate?.ex_rate ? toCurrencyFormat(Math.floor(parseInt(rate.ex_rate))) : '-'}
                </CurrencyValue>
              </CurrencyPair>
            </ExchangeRateRow>
            <ValidityRow>
              <ValidityInfo>
                <ValidityLabel>{t('topUpCrypto.depositAddress.rateValidFor')}</ValidityLabel>
                <CountdownTimer
                  validTime={validTime}
                  showRefreshButton={true}
                  onRefresh={handleRefreshRate}
                  isRefreshing={isRefreshing}
                />
              </ValidityInfo>
              <FixedQuoteButtonWrapper>
                <ButtonWithIcon
                  onClick={() => navigate('/top-up-crypto-preview', {
                    state: {
                      id: assetId,
                      iconUrl,
                      ticker,
                      network,
                      cryptoName,
                      decimal,
                    }
                  })}
                  icon={<></>}
                  text={t('topUpCrypto.depositAddress.getFixedQuote')}
                />
              </FixedQuoteButtonWrapper>
            </ValidityRow>

          </DepositCard>
          <AddressesWrapper>
            {isFromPreview && rate?.size && (
              <CopyableAddress
                label={t('topUpCrypto.depositAddress.transferAmount')}
                address={toCurrencyFormatWithDecimal(roundAmount(Number(rate.size), 'up', decimal), decimal)}
                onCopy={() => { }}
              />
            )}
            <CopyableAddress
              label={t('topUpCrypto.depositAddress.depositAddress')}
              address={depositAddress}
              onCopy={() => { }}
            />
            <ButtonWithIcon
              onClick={() => setIsBottomSheetOpen(true)}
              icon={<QRIcon fill={DARK_VIOLET} />}
              text={t('topUpCrypto.depositAddress.showQR')}
            />
            <FooterText>
              {t('topUpCrypto.depositAddress.sendOnlyToken', { crypto: cryptoName, network })}
            </FooterText>
          </AddressesWrapper>
        </CardContentWrapper>
        <ButtonContainer>
          <PrimaryButton
            variant="outline"
            onClick={() => navigate('/home')}
          >
            {t('topUpCrypto.depositAddress.backToHomeButton')}
          </PrimaryButton>
        </ButtonContainer>
      </Content>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        height="480px"
        header={
          <BottomSheetHeader title={t('topUpCrypto.depositAddress.qrCode')} />
        }
      >
        <QRCodeContainer>
          <div ref={qrCodeRef}>
            <QRCodeSVG value={depositAddress} minVersion={4} size={180} />
          </div>
          <ShareButtonContainer>
            <ButtonWithIcon
              onClick={captureAndShareQRCode}
              icon={<ShareIcon fill={DARK_VIOLET} />}
              text={t('topUpCrypto.depositAddress.shareAsImage')}
              disabled={isCapturing}
            />
          </ShareButtonContainer>
          <AlertBox
            alertText={t('topUpCrypto.depositAddress.alertText', { crypto: cryptoName })}
            iconExclamationColor={WHITE}
            iconFillColor={CRIMSON_500}
            iconOutlineColor={CRIMSON_500}
            backgroundColor="transparent"
            borderColor="transparent"
            fontFamily={INTER.regular}
          />
        </QRCodeContainer>
      </BottomSheet>
    </Container>
  )
}

export default TopUpCryptoDepositAddress

const ExchangeRateIcon = () => {
  return (
    <svg width="19" height="15" viewBox="0 0 19 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 5.29216C-2.32771e-08 4.74152 0.446381 4.29514 0.997021 4.29514L14.9765 4.29513L12.3349 1.65356C11.9567 1.27529 11.9567 0.661982 12.3349 0.283709C12.7301 -0.111452 13.3768 -0.0911665 13.7464 0.327986L18.1557 5.32825C18.4851 5.70175 18.2199 6.28918 17.7219 6.28918L0.99702 6.28918C0.446381 6.28918 2.32771e-08 5.84279 0 5.29216Z" fill="url(#paint0_linear_14413_607)" />
      <path d="M18.4041 8.8912C18.4041 9.44184 17.9577 9.88822 17.4071 9.88822L3.42755 9.88822L6.06913 12.5298C6.4474 12.9081 6.4474 13.5214 6.06913 13.8996C5.67396 14.2948 5.02731 14.2745 4.65769 13.8554L0.24837 8.8551C-0.0809873 8.48161 0.184204 7.89418 0.682177 7.89418H17.4071C17.9577 7.89418 18.4041 8.34056 18.4041 8.8912Z" fill="url(#paint1_linear_14413_607)" />
      <defs>
        <linearGradient id="paint0_linear_14413_607" x1="-0.588931" y1="-0.691478" x2="18.7751" y2="16.3267" gradientUnits="userSpaceOnUse">
          <stop offset="0.065" stopColor="#C9ECFF" />
          <stop offset="0.36" stopColor="#50A2FF" />
          <stop offset="0.67" stopColor="#7B69FF" />
          <stop offset="0.94" stopColor="#9B1BFF" />
        </linearGradient>
        <linearGradient id="paint1_linear_14413_607" x1="-0.588931" y1="-0.691478" x2="18.7751" y2="16.3267" gradientUnits="userSpaceOnUse">
          <stop offset="0.065" stopColor="#C9ECFF" />
          <stop offset="0.36" stopColor="#50A2FF" />
          <stop offset="0.67" stopColor="#7B69FF" />
          <stop offset="0.94" stopColor="#9B1BFF" />
        </linearGradient>
      </defs>
    </svg>

  )
}